import { useState, useEffect } from 'react';
import { ArrowRight, ArrowLeft, Info } from 'lucide-react';
import type { MatrixItem } from '../types';

interface ScoringViewProps {
  item: MatrixItem;
  itemIndex: number;
  totalItems: number;
  phase: 'impact' | 'effort';
  onScoreUpdate: (field: 'impact' | 'effort', value: number | null) => void;
  onNext: () => void;
  onBack: () => void;
  canProceed: boolean;
  progress: number;
}

export function ScoringView({
  item,
  itemIndex,
  totalItems,
  phase,
  onScoreUpdate,
  onNext,
  onBack,
  canProceed,
  progress,
}: ScoringViewProps) {
  const [inputValue, setInputValue] = useState<string>(
    phase === 'impact' ? (item.impact?.toString() || '') : (item.effort?.toString() || '')
  );

  useEffect(() => {
    const value = phase === 'impact' ? (item.impact?.toString() || '') : (item.effort?.toString() || '');
    setInputValue(value);
  }, [phase, item]);

  const handleInputChange = (value: string) => {
    setInputValue(value);
    const numValue = value.trim() === '' ? null : parseFloat(value);
    if (numValue !== null && !isNaN(numValue) && numValue >= 1 && numValue <= 5) {
      onScoreUpdate(phase, numValue);
    } else {
      onScoreUpdate(phase, null);
    }
  };

  const getPhaseInfo = () => {
    if (phase === 'impact') {
      return {
        title: 'Impact',
        description: 'How valuable or beneficial is this item? Higher = more valuable.',
        placeholder: '1-5',
        example: '5 = very high impact, 1 = low impact',
      };
    } else {
      return {
        title: 'Effort',
        description: 'How difficult or expensive is this item? Higher = more difficult/expensive.',
        placeholder: '1-5',
        example: '5 = very high effort, 1 = low effort',
      };
    }
  };

  const phaseInfo = getPhaseInfo();
  const isLastItem = phase === 'effort' && itemIndex === totalItems - 1;

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      {/* Progress */}
      <div>
        <div className="flex justify-between text-sm text-[var(--text-ter)] mb-2">
          <span>Item {itemIndex + 1} of {totalItems}</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <div className="w-full bg-[var(--bg-raised)] rounded-full h-1.5">
          <div
            className="bg-consulting-slate-blue-500 h-1.5 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Current Item */}
      <div className="p-4 bg-[var(--bg-raised)] border border-[var(--rule)]">
        <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-1">{item.name}</h2>
        <div className="text-sm text-[var(--text-ter)]">
          {phase === 'impact' && (
            <span>Impact: {item.impact !== null ? item.impact : 'Not set'}</span>
          )}
          {phase === 'effort' && (
            <>
              <span>Impact: {item.impact}</span>
              <span className="mx-2">·</span>
              <span>Effort: {item.effort !== null ? item.effort : 'Not set'}</span>
            </>
          )}
        </div>
      </div>

      {/* Phase question */}
      <div className="space-y-3">
        <div className="flex items-start gap-2">
          <Info className="h-4 w-4 text-consulting-slate-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-base font-semibold text-[var(--text-pri)]">{phaseInfo.title}</h3>
            <p className="text-sm text-[var(--text-ter)] mt-0.5">{phaseInfo.description}</p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[var(--text-ter)] mb-1.5">
            {phaseInfo.title} for <strong className="text-[var(--text-pri)]">{item.name}</strong> (1–5 scale)
          </label>
          <input
            type="number"
            value={inputValue}
            onChange={(e) => handleInputChange(e.target.value)}
            placeholder={phaseInfo.placeholder}
            min="1"
            max="5"
            step="1"
            className="w-full px-4 py-3 bg-[var(--bg-raised)] border border-[var(--rule)] text-[var(--text-pri)] placeholder-neutral-500 text-lg focus:outline-none focus:ring-2 focus:ring-consulting-slate-blue-500 focus:border-consulting-slate-blue-500"
            autoFocus
          />
          <p className="text-xs text-[var(--text-ter)] mt-1.5">Example: {phaseInfo.example}</p>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex gap-3 pt-1">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-[var(--text-ter)] border border-[var(--rule)] hover:bg-[var(--bg-raised)] transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <button
          onClick={onNext}
          disabled={!canProceed}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-consulting-slate-blue-600 hover:bg-consulting-slate-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {isLastItem ? 'View Results' : 'Next'}
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
